import { showToast } from '../components/Common/Toast';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import stripeSubscriptionService from '../services/StripeSubscriptionService';
import { useEmployeesList, useBlockingTimeOffCount, useOffTodayEmployeeIds } from '../hooks/useEmployeesPageData';
// eslint-disable-next-line no-unused-vars
import ModernPageHeader, { ModernStatCard, ModernActionButton } from '../components/Common/ModernPageHeader';
// eslint-disable-next-line no-unused-vars
import ModernCard from '../components/Common/ModernCard';
// eslint-disable-next-line no-unused-vars
import PageHeader from '../components/Common/PageHeader';
// eslint-disable-next-line no-unused-vars
import ModernTable from '../components/Common/ModernTable';
import EmployeeInviteModal from '../components/EmployeeInviteModal';
import EmployeeImportModal from '../components/employees/EmployeeImportModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { useConfirmation } from '../hooks/useConfirmation';
import useFocusTrap from '../hooks/useFocusTrap';
import TimeOffPanel from '../components/employees/TimeOffPanel';
// eslint-disable-next-line no-unused-vars
import { prepareCompanyData, logCompanyOperation } from '../utils/companyUtils';
import { getAvailableRoles, getRoleDisplayName, getRoleBadgeColor } from '../utils/roleUtils';
// eslint-disable-next-line no-unused-vars
import { canManagePermissions } from '../utils/simplePermissions';
import { getInitials } from '../utils/avatarUtils';
import { supabase } from '../utils/supabaseClient';
// createClient removed — use imported supabase client for proper auth + RLS
import { generateTempPassword, getDefaultPermissions, EMPLOYEE_MODULES, isCrewSeatRole, FULL_TIER_ONLY_MODULES } from '../utils/employeePermissions';
import { formatPhoneDisplay } from '../utils/phone';
import {
  UsersIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  UserGroupIcon,
  EyeSlashIcon,
  PowerIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import {
  XCircleIcon as XCircleIconSolid,
  ClockIcon as ClockIconSolid
} from '@heroicons/react/24/solid';
import '../styles/modern-enhancements.css';
import { useUser } from '../contexts/UserContext';

import SkillsPicker from '../components/SkillsPicker';
import { EmployeesStats, EmployeesSearchFilter, EmployeesTable } from '../components/employees';
import { fmtDate } from '../utils/dateFormatters';
import {
  PAY_TYPES,
  STANDARD_ANNUAL_HOURS,
  canSeeCompensation,
  hourlyCostFromSalary,
  resolveCompensation,
  roundRate,
  splitCompensationPayload,
  upsertEmployeeCompensation,
  withoutCompensation,
} from '../utils/employeeCompensation';
import { isOwnerOrAdmin } from '../utils/simplePermissions';
import PhoneField from '../components/Common/PhoneField';

// Legacy environment variables for beta (will be removed after full Supabase client migration)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;



// Sequential per-company employee number from the canonical generator — the same RPC that
// numbers invoices, quotes, work orders and POs (generate_document_number). This used to be
// `EMP-${Date.now()}`, which produced EMP-1788038436333. Falls back to the old timestamp if
// the RPC fails so re-activating an employee is never blocked by numbering.
/**
 * The ONE source of an employee number. Board #453.
 *
 * generate_document_number locks the per-company counter, increments it and returns
 * PREFIX-YYYY-#### — the same allocator that numbers invoices, quotes, work orders and POs, so
 * two admins can never be handed the same value.
 *
 * NO `EMP-${Date.now()}` FALLBACK any more. It looked like resilience and was the opposite: it
 * silently invented an identifier in a format nothing else uses the moment the allocator
 * failed, and those survive in the live data (EMP-1769278269762 is one). A timestamp is not a
 * number in the company's sequence, it never appears in the Employees list in order, and it
 * hides the failure that produced it. Throwing means the caller reports that the number could
 * not be issued, which is the truth and is fixable.
 */
const nextEmployeeNumber = async (companyId) => {
  const { data, error } = await supabase.rpc('generate_document_number', {
    p_company_id: companyId,
    p_doc_type: 'employee',
  });
  if (error) throw error;
  if (!data) throw new Error('Could not issue an employee number. Please try again.');
  return data;
};

const NEW_EMPLOYEE_FORM = {
  email: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  phone_number: '',
  role: 'technician',
  is_schedulable: true,
  pay_type: 'hourly',
  hourly_rate: '',
  overtime_rate: '',
  annual_salary: '',
  department: '',
  capacity_hours_per_day: '8',
  job_title: ''
};

const NO_OFF_TODAY = new Set();

const Employees = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  // Board #2679: styled confirmation for Reactivate (the window.confirm calls
  // elsewhere in this file are untouched — separate finding).
  const { modal: confirmDialog, confirm } = useConfirmation();
  // Board #451: may this person see and set pay? Owners/admins always; everyone else needs the
  // existing `payroll` permission, which is defaultEnabled:false and role-granted to accountants.
  const showCompensation = canSeeCompensation(user);
  const queryClient = useQueryClient();
  
  // React Query hooks for data fetching
  const { data: employeesData, isLoading: loadingEmployees, error: employeesError } = useEmployeesList(user?.company_id);
  // PTO Requests card. This used to be a hardcoded 0 — see the hook's note.
  const { data: blockingTimeOffCount = 0 } = useBlockingTimeOffCount(user?.company_id);
  // "Off today" badge + filter: time off in effect covering today (#6154, 2026-10-24 ruling).
  // Separate from the HR status "On Leave", which stays as it is.
  const { data: offTodayIds = NO_OFF_TODAY } = useOffTodayEmployeeIds(user?.company_id);
  
  // Derived state from queries
  const employees = employeesData || [];
  const loading = loadingEmployees;
  
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  // Board #456: ?tab= lets the time-off notification land on the company-wide list rather
  // than on the Team Members tab, where the reader would have to know to go looking. Same
  // idea as the existing ?new= convention on Quotes/Customers/Expenses (CLAUDE.md §19) —
  // reuse the mechanism rather than inventing a second one.
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(window.location.search).get('tab') === 'time-off' ? 'pto-management' : 'employees'
  );

  // Open the primary (email invite) add flow when arrived via ?new=employee — e.g. the command
  // palette "Add New Employee" (mirrors the ?new= pattern in Quotes/Customers/Expenses).
  const employeeLocation = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(employeeLocation.search);
    if (params.get('new') !== 'employee') return;
    setShowInviteModal(true);

    // CONSUME the parameter. This effect only re-runs when the search STRING changes, and
    // all three entry points navigate to the identical "?new=employee" -- the command
    // palette and both dashboard "Add Employee" cards. Leaving it set makes every click
    // after the first a no-op for the life of the page. Same trap QuotesPro documents at
    // pages/QuotesPro.js:322.
    params.delete('new');
    const rest = params.toString();
    navigate(`${employeeLocation.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
  }, [employeeLocation.search]);

  // Board #2125: the same convention for EDITING one employee. Labor Costs has a per-row
  // "Edit employee pay rates" button that used to navigate to the bare /employees list,
  // dropping which employee you had clicked — you arrived at 20 rows and had to find them
  // again. It now sends ?edit=<id>. Waits for the list to load, because the row has to exist
  // before it can be opened, and consumes the parameter for the same reason ?new= does.
  useEffect(() => {
    const params = new URLSearchParams(employeeLocation.search);
    const editId = params.get('edit');
    if (!editId || employees.length === 0) return;
    const match = employees.find(e => String(e.id) === String(editId));
    if (match) editEmployee(match);
    params.delete('edit');
    const rest = params.toString();
    navigate(`${employeeLocation.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
  }, [employeeLocation.search, employees.length]); // eslint-disable-line react-hooks/exhaustive-deps


	  const [sortBy, setSortBy] = useState({ key: 'name', dir: 'asc' });

  // New state for enhanced features


  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  // ?status= lets a tile elsewhere open this list on the same set it counted (Scheduling's
  // "Active Technicians" counts get_schedulable_employees and used to land on all members).
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = new URLSearchParams(window.location.search).get('status');
    return ['schedulable', 'active', 'inactive', 'pending', 'on_leave', 'off_today', 'terminated'].includes(s) ? s : 'all';
  });
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [tempPasswordAlert, setTempPasswordAlert] = useState({ show: false, email: '', password: '', reset: false, name: '' });
  const [confirmModal, setConfirmModal] = useState(null);
  const [formData, setFormData] = useState(NEW_EMPLOYEE_FORM);
  // ✅ NEW: Use unified module structure from employeePermissions.js
  const [newEmployeePermissions, setNewEmployeePermissions] = useState(() =>
    getDefaultPermissions('technician') // Default permissions for technician role
  );
  const [formLoading, setFormLoading] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  // Escape closes whichever employee modal is open. The file had no key handler of
  // any kind, so both the Add and the Edit dialog could only be left via their own
  // buttons. Only ONE handler for both, keyed off which flag is set, because two
  // independent listeners would both fire and the second would try to close a dialog
  // that was never open.
  //
  // Deliberately NOT closing on backdrop click for these two: both hold a long,
  // partly-filled employee form, and a stray click outside would discard typing with
  // no undo. Escape is a deliberate keystroke; a misplaced click is not. The Add
  // Auto-Accept Rule modal got backdrop dismissal because it is four short fields.
  useEffect(() => {
    if (!showCreateForm && !showEditForm) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (showEditForm) {
        // Same as the Edit dialog's Cancel: Add and Edit share formData, so leaving the
        // edited employee in it pre-filled the next Add New Employee (#5570).
        setShowEditForm(false);
        setEditingEmployee(null);
        setFormData(NEW_EMPLOYEE_FORM);
      } else setShowCreateForm(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showCreateForm, showEditForm]);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const createTrapRef = useFocusTrap(showCreateForm);
  const editTrapRef = useFocusTrap(showEditForm && !!editingEmployee);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState({
    quotes: false,
    jobs: false,
    customers: false,
    documents: false,
    employees: false,
    settings: false,
    calendar: false,
    invoices: false,
    reports: false
  });

  // "Can see all jobs" (crew seats; 20261022_tech_assigned_jobs_only). Offered only once the
  // column exists, so the edit form keeps saving before that migration is applied.
  const [editingSeeAllJobs, setEditingSeeAllJobs] = useState(false);
  const [seeAllJobsSupported, setSeeAllJobsSupported] = useState(false);

  // Skills editing state (Phase 1)
  const [editingEmployeeRowId, setEditingEmployeeRowId] = useState(null);
  const [editingEmployeeSkills, setEditingEmployeeSkills] = useState([]); // [{skill_id, level}]
  const [originalEmployeeSkills, setOriginalEmployeeSkills] = useState([]);


	  // Detail slide-over state
	  const [showDetailPanel, setShowDetailPanel] = useState(false);
	  const [detailEmployee, setDetailEmployee] = useState(null);
	  const [detailPermissions, setDetailPermissions] = useState(null);
	  const openDetailPanel = async (employee) => {
	    setDetailEmployee(employee);
	    setShowDetailPanel(true);
	    setDetailPermissions(null);
	    // A "No login" employee has no user_id; eq('user_id', null) is sent as the string
	    // 'null' and 400s on every click (board #5830). They have no permissions row to show.
	    if (!employee?.user_id) return;
	    // Load the employee's module permissions the same way the Edit modal does
	    // (from employee_permissions, keyed by module.key). null → "No permissions record".
	    try {
	      const { data: permData, error: permError } = await supabase
	        .from('employee_permissions')
	        .select('*')
	        .eq('user_id', employee.user_id)
	        .eq('company_id', user.company_id);
	      if (!permError && permData && permData.length > 0) {
	        const dbPerms = permData[0];
	        const loadedPerms = {};
	        Object.keys(EMPLOYEE_MODULES).forEach(moduleKey => {
	          const module = EMPLOYEE_MODULES[moduleKey];
	          loadedPerms[module.key] = dbPerms[module.key] === true || dbPerms[module.key] === 'true' || dbPerms[module.key] === 't';
	        });
	        setDetailPermissions(loadedPerms);
	      }
	    } catch (e) {
	      console.warn('Failed loading employee permissions for detail panel', e);
	    }
	  };
	  const closeDetailPanel = () => { setShowDetailPanel(false); setDetailEmployee(null); setDetailPermissions(null); };

	  // Board #449: the quick-view Role and Phone inline editors are gone (display-only per the
	  // operator ruling), and their handlers went with them rather than being left unreferenced.
	  // saveDetailRole wrote users.role straight from the client, which the own-row-only UPDATE
	  // policy on that table made a guaranteed zero-row no-op for anybody but yourself;
	  // saveDetailPhone did go through the edge function but ignored user_data_result.success, so
	  // a nested failure still toasted "Phone updated". Both fields are edited in the full Edit
	  // flow, which holds the service-role path and verifies its own writes.
	  const handleActivateEmployee = async (employeeId) => {
	    try {
	      // ONE WRITE: employees.status. users.status is mirrored by the database.
	      //
	      // This used to update `users` from the client first and, since board #449 added a
	      // zero-row guard, THROW when that matched nothing — which is every employee but
	      // yourself, because the only UPDATE policy on users is `auth_user_id = auth.uid()`
	      // (verified live 2026-09-19). The guard was right about the write being a no-op and
	      // wrong about what to do next: it turned a silent failure into a total one.
	      //
	      // The client cannot write another user's row and should not try. Migration
	      // 20260919_employee_status_revokes_company_access (board #1240) makes
	      // trg_mirror_employee_status_to_user carry employees.status across to users.status,
	      // so the single write below now does what three scattered client writes were failing
	      // to do — and it works for the 2 employees who have no linked user row at all.
	      const { data: activated, error: empError } = await supabase
	        .from('employees')
	        .update({ status: 'active' })
	        .eq('id', employeeId)
	        .eq('company_id', user.company_id)
	        .select('id');

	      if (empError) throw empError;
	      if (!activated || activated.length === 0) {
	        throw new Error('Employee was not activated — no matching record. Refresh and try again.');
	      }

	      showToast('success','Employee activated');
	      queryClient.invalidateQueries({ queryKey: ['employees-list'] });
	      setDetailEmployee(prev=> prev? { ...prev, status:'active' }:prev);
	    } catch(e){ console.error(e); showToast('error', e.message || 'Failed to activate'); }
	  };
	  // Board #4433: this used to copy `/accept-invite?token=<random>` — a token nothing stored
	  // and a route that does not exist. The admin-invite-user edge function creates the auth
	  // user via generateLink({ type: 'invite' }) and has no re-send path for an existing
	  // pending user, so there is nothing valid to copy yet. The control stays visible but
	  // disabled until a resend exists server-side.


	  // Detail panel extra state
	  const [detailAuth, setDetailAuth] = useState(null);



  const showAlert = (type, message, duration = 5000) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), duration);
  };

  /**
   * A Crew seat can never hold a Full-tier module: getEffectivePermissions applies that cap
   * LAST and it cannot be overridden by a stored toggle. The editor did not know that, so
   * switching Employees or Payroll on for a lead technician saved `true` and the runtime
   * stripped it on every read — the switch stayed on, the access never arrived, and nothing
   * on screen explained why. (Time Tracking is NOT capped and was saving correctly.)
   */
  const isSeatLockedModule = (moduleKey, role) =>
    isCrewSeatRole(role) && FULL_TIER_ONLY_MODULES.includes(moduleKey);

  /**
   * Applied at SAVE as well as at the toggle, because the role can change in the same edit:
   * switch someone from dispatcher to technician with Payroll already on, and the toggle
   * guard was never reached for a switch nobody touched. Storing that true would leave a row
   * the runtime has to strip on every read, which is the state this finding is about.
   */
  const withSeatCap = (permissions, role) => {
    if (!isCrewSeatRole(role)) return permissions;
    const capped = { ...permissions };
    FULL_TIER_ONLY_MODULES.forEach((key) => {
      if (key in capped) capped[key] = false;
    });
    return capped;
  };

  const handlePermissionToggle = (permission) => {
    if (isSeatLockedModule(permission, formData.role)) return;
    setNewEmployeePermissions(prev => ({
      ...prev,
      [permission]: !prev[permission]
    }));
  };

  const EXPORT_STATUS_LABELS = { active: 'Active', inactive: 'Inactive', pending_invite: 'Pending Invite' };
  const exportEmployeesCSV = (rows) => {
    const headers = ['Full Name','Email','Role','Phone','Status','Schedulable','Joined'];
    const lines = rows.map(emp => [
      JSON.stringify(emp.full_name||''),
      JSON.stringify(emp.email||''),
      // Labels, not raw enums (board #4420): the import accepts the display labels, so the
      // exported file round-trips, and "lead_technician" / "pending_invite" never reach a
      // spreadsheet. Status labels mirror the badge map in EmployeesTable.
      JSON.stringify(emp.role ? getRoleDisplayName(emp.role) : ''),
      // `phone`, not `phone_number` — the same correction already made at the detail panel
      // (see the two ✅ FIX notes below) and in the edit form, but missed here, so the Phone
      // column exported blank for every employee (board #942). There is no phone_number
      // column anywhere: employees has only emergency_contact_phone, and the number comes
      // from users.phone. `phone_number` survives as a FORM field name and in the invite
      // payload, which the edge function maps — so only this read was wrong.
      // Same display value as the table column (EmployeesTable.js), not the raw digits (board #5015).
      JSON.stringify(emp.phone ? formatPhoneDisplay(emp.phone) : ''),
      JSON.stringify(EXPORT_STATUS_LABELS[emp.status || (emp.active ? 'active':'inactive')] || (emp.status || '')),
      // The table shows Schedulable; the export dropped it (board #4289).
      JSON.stringify(emp.is_schedulable === false ? 'No' : 'Yes'),
      JSON.stringify(emp.created_at ? fmtDate(emp.created_at) : '')
    ].join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'employees.csv'; a.click(); URL.revokeObjectURL(url);
  };

  const handleInvite = async (inviteData) => {
    try {
      console.log('🚀 ========== EMPLOYEE INVITE VIA EDGE FUNCTION ==========');
      console.log('📧 Email:', inviteData.email);
      console.log('👤 Name:', `${inviteData.first_name} ${inviteData.middle_name || ''} ${inviteData.last_name}`.trim());
      console.log('🎭 Role:', inviteData.role);

      // Get the current user's session token for auth
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be logged in to invite employees');
      }

      // Call Edge Function to invite user securely (uses service role key server-side)
      console.log('🔐 Calling admin-invite-user Edge Function...');
      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-invite-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: inviteData.email,
          first_name: inviteData.first_name,
          middle_name: inviteData.middle_name || null,
          last_name: inviteData.last_name,
          phone_number: inviteData.phone || null,
          role: inviteData.role.toLowerCase(),
          is_schedulable: true,
          permissions: inviteData.permissions || null,
          // Same compensation owner as the other two save paths (board #452), and board
          // #451 strips it entirely for anyone without the payroll permission - omitting the
          // keys leaves the stored columns untouched rather than blanking them.
          ...(showCompensation ? resolveCompensation(inviteData) : withoutCompensation(resolveCompensation(inviteData))),
          department: inviteData.department || '',
          capacity_hours_per_day: parseFloat(inviteData.capacity_hours_per_day) || 8,
          job_title: inviteData.job_title || inviteData.role
        })
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Edge Function error:', result);
        throw new Error(result.error || 'Failed to invite employee');
      }

      console.log('✅ Invite sent successfully:', result);
      console.log('🎉 ========== EMPLOYEE INVITE COMPLETE ==========');

      // A 200 is not the same as "it all worked". The function reports partial failures in the
      // body — it cannot throw once the auth user exists without leaving a half-done invite
      // nobody can see. Checking only response.ok showed a green "Invite sent!" toast for an
      // employee row that never got created, which is the same swallow as board #449.
      if (result.employee_error) {
        showToast('error', `${inviteData.email} has a login, but their employee record could not be created: ${result.employee_error}`);
      } else if (result.email_sent === false) {
        showToast('warning', `${inviteData.email} was added, but the invitation email could not be sent. Send them their set-password link manually.`);
      } else {
        showToast('success', `✅ Invite sent to ${inviteData.email}! They will receive an email to set their password.`);
      }
      queryClient.invalidateQueries({ queryKey: ['employees-list'] }); // Refresh the list

    } catch (error) {
      console.error('❌ ========== EMPLOYEE INVITE FAILED ==========');
      console.error('❌ Error:', error);
      showToast('error', error.message);
      // #939: rethrow so the invite modal stays open and shows the reason. Swallowing it here
      // let the modal close and discard the form, leaving a toast that was gone in ~2s.
      throw error;
    }
  };

  /**
   * One CSV row -> one invitation (board #950). Returns {ok, error?, warning?} rather than
   * throwing or toasting, because the import modal reports every row individually and a
   * fifty-row import must not produce fifty toasts.
   *
   * Reads the SAME partial-failure fields as handleInvite: the edge function answers 200 while
   * reporting a failed employee row or a failed email in the body, and treating 200 as success
   * is precisely the false-success shape board #449 was about. An employee row that did not get
   * created is a FAILURE here — an auth account nobody can see on this page is worse than a
   * row the importer can retry.
   */
  const importEmployeeRow = async (row) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return { ok: false, error: 'Signed out — sign in again and retry.' };

      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-invite-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: row.email,
          first_name: row.first_name,
          middle_name: null,
          last_name: row.last_name,
          phone_number: row.phone || null,
          role: row.role,
          is_schedulable: true,
          permissions: null,
          department: row.department || '',
          capacity_hours_per_day: 8,
          job_title: row.job_title || row.role
          // No compensation keys at all. Omitting them leaves the columns at their defaults,
          // which is the same treatment board #451 gives an inviter without the payroll
          // permission — and a CSV is not where anyone should be setting pay rates.
        })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) return { ok: false, error: result.error || `Failed (${response.status})` };
      if (result.employee_error) return { ok: false, error: `Login created but no employee record: ${result.employee_error}` };
      if (result.email_sent === false) return { ok: true, warning: 'invitation email did not send' };
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message || 'Failed' };
    }
  };

  const checkUserExists = async (email) => {
    try {
      // Check users table for existing email
      // Note: Auth-level duplicate check is handled by the Edge Function
      console.log(`🔍 Checking if user exists in database: ${email}`);

      const { data: existingUsers, error } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', email.toLowerCase())
        .limit(1);

      if (error) {
        console.error('Error checking user existence:', error);
        return false; // Let Edge Function handle the final check
      }

      if (existingUsers && existingUsers.length > 0) {
        console.log(`❌ Found existing user in DB: ${existingUsers[0].email}`);
        return true;
      }

      console.log(`✅ No existing user found in database`);
      return false;
    } catch (error) {
      console.error('Error checking user existence:', error);
      return false; // Let Edge Function handle the final check
    }
  };

  const createEmployee = async (e) => {
    e.preventDefault();

    const { email, first_name, middle_name, last_name, phone_number, role, is_schedulable, pay_type, hourly_rate, overtime_rate, department, capacity_hours_per_day, job_title, employee_number, hire_date, termination_date, employee_type, emergency_contact_name, emergency_contact_phone, emergency_contact_relationship, date_of_birth, pto_balance_hours, status, manager_id, certifications, notes } = formData;

    // Validation
    if (!email || !first_name || !last_name || !role) {
      showToast('error', 'Please fill in email, first name, last name, and role');
      return;
    }

    setFormLoading(true);

    try {
      console.log('🚀 ========== EMPLOYEE CREATION VIA EDGE FUNCTION ==========');
      console.log('📧 Email:', email);
      console.log('👤 Name:', `${first_name} ${middle_name || ''} ${last_name}`.trim());
      console.log('🎭 Role:', role);
      console.log('📅 Schedulable:', is_schedulable);

      // Generate temporary password
      const tempPassword = generateTempPassword();
      console.log('🔐 Temp password generated:', tempPassword ? '***hidden***' : 'MISSING');

      // Get the current user's session token for auth
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be logged in to create employees');
      }

      // Call Edge Function to create user securely (uses service role key server-side)
      console.log('🔐 Calling admin-create-user Edge Function...');
      const requestBody = {
        email,
        password: tempPassword,
        first_name,
        middle_name: middle_name || null,
        last_name,
        phone_number: phone_number || null,
        role: role.toLowerCase(),
        is_schedulable: is_schedulable !== undefined ? is_schedulable : true,
        permissions: withSeatCap(newEmployeePermissions, role),
        // Board #452: one owner for the compensation columns, shared with the edit path below.
        // For a salaried employee hourly_rate is DERIVED from annual_salary, so job costing
        // keeps reading the single canonical cost-per-hour column.
        ...(showCompensation ? resolveCompensation(formData) : withoutCompensation(resolveCompensation(formData))),
        department: formData.department || '',
        capacity_hours_per_day: parseFloat(formData.capacity_hours_per_day) || 8,
        job_title: formData.job_title || role
      };
      console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));
      
      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-create-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      console.log('📥 Edge Function response:', result);

      if (!response.ok) {
        console.error('❌ Edge Function error:', result);
        throw new Error(result.error || 'Failed to create employee');
      }

      // The edge function reports a failed employee row in the BODY — it cannot throw once the
      // auth user exists without leaving a half-done create nobody can see. Checking only
      // response.ok showed the green "Employee Created Successfully!" card, with a temp
      // password, for somebody who had a login and no employee record: not in the table, not
      // in Pending Invites, no explanation. Same handling as the invite path above.
      if (result.employee_error) {
        showToast('error', `${email} has a login, but their employee record could not be created: ${result.employee_error}`);
        return;
      }

      console.log('✅ Employee created successfully:', result);
      console.log('🎉 ========== EMPLOYEE CREATION COMPLETE ==========');

      // Show temp password alert with copy button
      setTempPasswordAlert({
        show: true,
        email: email,
        password: tempPassword
      });

      showToast('success', `Employee ${first_name} ${last_name} created successfully!`);

      // Reset form
      setFormData({ 
        email: '', 
        first_name: '', 
        middle_name: '', 
        last_name: '', 
        phone_number: '', 
        role: 'technician', 
        is_schedulable: true,
        pay_type: 'hourly',
        hourly_rate: '',
        overtime_rate: '',
        annual_salary: '',
        department: '',
        capacity_hours_per_day: '8',
        job_title: ''
      });
      setNewEmployeePermissions(getDefaultPermissions('technician'));
      setShowCreateForm(false);
      queryClient.invalidateQueries({ queryKey: ['employees-list'] }); // Refresh the list

    } catch (error) {
      console.error('❌ Error creating employee:', error);
      showToast('error', error.message);
    } finally {
      setFormLoading(false);
    }
  };

  // ✅ REMOVED: generateTempPassword now imported from employeePermissions.js

  // Calculate employee analytics
  const getEmployeeAnalytics = () => {
    const totalEmployees = employees.length;
    const activeEmployees = employees.filter(emp => emp.status === 'active' || emp.active === true).length;
    const pendingInvites = employees.filter(emp => emp.status === 'pending_invite').length;
    // "Can be assigned to jobs" = schedulable AND active, the rule the assignment picker
    // (get_schedulable_employees) applies: the flag alone counted 3 pending invites and 5
    // inactive people, 30 against 22 assignable (audit 2026-10-17).
    const isAssignable = (emp) => emp.is_schedulable === true && (emp.status === 'active' || (emp.status == null && emp.active === true));
    const schedulableEmployees = employees.filter(isAssignable).length;
    // Strict: only employees explicitly marked inactive (NOT "everyone not active",
    // which double-counted Pending Invites — audit U2, 2026-07-14).
    const inactiveEmployees = employees.filter(emp => emp.status === 'inactive').length;
    const ptoRequestsCount = blockingTimeOffCount;

    return {
      totalEmployees,
      activeEmployees,
      pendingInvites,
      inactiveEmployees,
      ptoRequestsCount,
      schedulableEmployees
    };
  };

  const analytics = getEmployeeAnalytics();

  // Filter employees based on search and filters
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = !searchTerm ||
      employee.full_name?.toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.trim().toLowerCase());

    const matchesRole = roleFilter === 'all'
      || (roleFilter === 'no_login' ? !employee.user_id : employee.role === roleFilter);
    const matchesStatus = statusFilter === 'all' ||
      // 'schedulable' answers the question the tile above asks — "who can I send to a job
      // today" — and it is NOT the same set as status='active'. It had no case here, so
      // the Schedulable tile clicked through to the Active filter instead: live 2026-08-28
      // on 48f32d34 the tile read 10 and the table showed 7, because 3 employees are
      // is_schedulable with a status other than active. Same defect as the Customers tiles
      // whose filter cases were missing.
      (statusFilter === 'schedulable' && employee.is_schedulable === true && (employee.status === 'active' || (employee.status == null && employee.active === true))) ||
      (statusFilter === 'active' && (employee.status === 'active' || employee.active === true)) ||
      (statusFilter === 'inactive' && (employee.status === 'inactive' || employee.active === false)) ||
      (statusFilter === 'pending' && employee.status === 'pending_invite') ||
      (statusFilter === 'on_leave' && employee.status === 'on_leave') ||
      (statusFilter === 'off_today' && offTodayIds.has(employee.id)) ||
      (statusFilter === 'terminated' && employee.status === 'terminated');



    return matchesSearch && matchesRole && matchesStatus;
  });

	  // #5673: sort Role by the label the column shows ("No login" for an employee without a
	  // user account), not the raw key — the raw key is empty for those rows, so they sorted first.
	  const sortedEmployees = useMemo(() => {
	    const roleLabel = (e) => (e.user_id ? getRoleDisplayName(e.role) : 'No login') || '';
	    const arr = [...filteredEmployees];
	    const k = sortBy.key; const d = sortBy.dir === 'asc' ? 1 : -1;
	    arr.sort((a,b)=>{
	      const av = k==='name' ? (a.full_name||'') : k==='email' ? (a.email||'') : k==='role' ? roleLabel(a) : k==='status' ? (a.status|| (a.active?'active':'inactive')) : (a.created_at||'');
	      const bv = k==='name' ? (b.full_name||'') : k==='email' ? (b.email||'') : k==='role' ? roleLabel(b) : k==='status' ? (b.status|| (b.active?'active':'inactive')) : (b.created_at||'');
	      if (av<bv) return -1*d; if (av>bv) return 1*d; return 0;
	    });
	    return arr;
	  }, [filteredEmployees, sortBy]);


  // Utility functions

  const getStatusBadge = (employee) => {
    const status = employee.status || (employee.active ? 'active' : 'inactive');
    const statusMap = {
      'active': { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      'inactive': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Inactive' },
      'pending_invite': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Invite' },
      // #6152: same gap as EmployeesTable -- other statuses fell back to green Active.
      'terminated': { bg: 'bg-red-100', text: 'text-red-800', label: 'Terminated' },
      'on_leave': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'On Leave' },
      'suspended': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Suspended' },
      'probation': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Probation' },
      'retired': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Retired' }
    };
    return statusMap[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
  };

	  // Bulk select (after filteredEmployees and sortedEmployees are declared)
	  const [selectedIds, setSelectedIds] = useState(new Set());
	  const allVisibleSelected = useMemo(()=>filteredEmployees?.length>0 && filteredEmployees.every(e=>selectedIds.has(e.id)), [filteredEmployees, selectedIds]);
	  const toggleSelectAll = () => {
	    setSelectedIds(prev => {
	      const next = new Set(prev);
	      const all = filteredEmployees.length>0 && filteredEmployees.every(e=>next.has(e.id));
	      filteredEmployees.forEach(e => { if (all) next.delete(e.id); else next.add(e.id); });
	      return next;
	    });
	  };
	  const toggleRow = (id) => setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });


	  // Bulk actions for selected employees
	  // #952: bulk actions only touch the rows they make sense for — Activate only Inactive rows,
	  // Deactivate only Active rows and never the owner or yourself. Pending invites are left alone
	  // in both directions (they never had a login to grant or revoke), matching the row buttons.
	  const statusOf = (e) => e.status || (e.active ? 'active' : 'inactive');
	  // Only rows the user can currently see: a row selected and then filtered away is not acted on.
	  const selectedRows = (filteredEmployees || []).filter((e) => selectedIds.has(e.id));
	  const bulkActivateIds = selectedRows.filter((e) => statusOf(e) === 'inactive').map((e) => e.id);
	  const bulkDeactivateIds = selectedRows
	    .filter((e) => statusOf(e) === 'active' && String(e.role || '').toLowerCase() !== 'owner' && (!e.user_id || e.user_id !== user?.id))
	    .map((e) => e.id);

	  const activateSelected = async () => {
	    const ids = bulkActivateIds;
	    if (!ids.length) return showToast('info', 'None of the selected employees are inactive');
	    const ok = await confirm({
	      title: `Reactivate ${ids.length} employee${ids.length === 1 ? '' : 's'}?`,
	      message: 'They will be able to sign in again and each counts as a billed seat.',
	      confirmText: 'Reactivate',
	      cancelText: 'Cancel',
	      isDangerous: false
	    });
	    if (ok !== 'confirm') return;
	    try {
	      // Bulk activate wrote `users.status` keyed on an employees.id — not even user_id —
	      // and never touched employees.status at all, so it changed nothing on either table:
	      // 328 of 334 employees have id <> user_id, and the own-row-only UPDATE policy on
	      // users would have rejected the other 6 anyway. Nothing checked the result, so it
	      // reported "Activated N employees" every time. Mirrors deactivateSelected now: write
	      // the one column the client owns, check it landed, and let
	      // trg_mirror_employee_status_to_user carry it to users.status.
	      const { data: activated, error: empErr } = await supabase
	        .from('employees')
	        .update({ status: 'active' })
	        .in('id', ids)
	        .eq('company_id', user.company_id)
	        .select('id');
	      if (empErr) throw empErr;

	      showToast('success', `Activated ${activated?.length || 0} employee${(activated?.length || 0) === 1 ? '' : 's'}`);
	      setSelectedIds(new Set());
	      queryClient.invalidateQueries({ queryKey: ['employees-list'] });

	      // A reactivated seat is a billed seat — same reason deactivateSelected syncs.
	      stripeSubscriptionService.syncSeats().catch((err) =>
	        console.error('Seat sync after activation failed:', err?.message || err)
	      );
	    } catch (e) {
	      console.error(e);
	      showToast('error', 'Failed to activate some employees');
	    }
	  };
	  const deactivateSelected = async () => {
	    const ids = bulkDeactivateIds;
	    if (!ids.length) return showToast('info', 'None of the selected employees can be deactivated');
	    const ok = await confirm({
	      title: `Deactivate ${ids.length} employee${ids.length === 1 ? '' : 's'}?`,
	      message: 'Their login is revoked; their history, jobs and timesheets are kept. You can reactivate them later.',
	      confirmText: 'Deactivate',
	      cancelText: 'Cancel',
	      isDangerous: true
	    });
	    if (ok !== 'confirm') return;
	    try {
	      const { data: deactivated, error: empErr } = await supabase
        .from('employees')
        .update({ status: 'inactive' })
        .in('id', ids)
        .eq('company_id', user.company_id)
        .select('id');
      if (empErr) throw empErr;

      // The follow-up `users` write that used to live here is gone. It could not work: the
      // only UPDATE policy on users is `auth_user_id = auth.uid()` (verified live 2026-09-19),
      // so bulk-updating other people's rows matched nothing — and the result was not even
      // checked, which is exactly how 2 employees ended up inactive with users.status still
      // active and kept their company access (board #1240). users.status is now mirrored by
      // trg_mirror_employee_status_to_user, so the write above is the whole job.

	      const n = deactivated?.length || 0;
	      showToast('success', `Deactivated ${n} employee${n === 1 ? '' : 's'}`);
	      setSelectedIds(new Set());
	      queryClient.invalidateQueries({ queryKey: ['employees-list'] });

	      // Billing is per ACTIVE user, so deactivating changes what they owe. Reconcile the
	      // Stripe seat quantities now rather than waiting for someone to open the billing
	      // page. Fire-and-forget: a sync failure must never fail the deactivation, and the
	      // billing screen re-syncs on load anyway.
	      stripeSubscriptionService.syncSeats().catch((err) =>
	        console.error('Seat sync after deactivation failed:', err?.message || err)
	      );
	    } catch (e) {
	      console.error(e);
	      showToast('error', 'Failed to deactivate some employees');
	    }
	  };

  // ✅ NEW: Toggle schedulable status directly from table
  const handleToggleSchedulable = async (employeeId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      console.log(`🔄 Toggling schedulable status for ${employeeId}: ${currentStatus} → ${newStatus}`);

      // Use authenticated Supabase client
      
      // Callers pass employee.id (the employees PK), so match on id — NOT user_id.
      // Matching on user_id never hit a row, so this fell into the insert fallback below
      // and wrote an employees.id into the user_id→users FK column (employees_user_id_fkey 23503).
      const { data: updated, error: updateError } = await supabase
        .from('employees')
        .update({ is_schedulable: newStatus, updated_at: new Date().toISOString() })
        .eq('id', employeeId)
        .eq('company_id', user.company_id)
        .select('id, is_schedulable');

      if (updateError || !updated || updated.length === 0) {
        // If no rows updated, create the employees record enriched with user data
        console.log('⚠️ No employees record found, creating one...');
        const { data: userData } = await supabase
          .from('users')
          .select('first_name, last_name, email, role')
          .eq('id', employeeId)
          .single();

        const { error: insertError } = await supabase
          .from('employees')
          .insert({
            user_id: employeeId,
            company_id: user.company_id,
            first_name: userData?.first_name || '',
            last_name: userData?.last_name || '',
            employee_number: await nextEmployeeNumber(user.company_id),
            is_schedulable: newStatus,
            status: 'active',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) throw new Error(insertError.message || 'Failed to create employee record');
      }

      showToast('success', newStatus ? '✅ Employee is now schedulable' : '⚠️ Employee removed from scheduling');
      queryClient.invalidateQueries({ queryKey: ['employees-list'] }); // Refresh the list
    } catch (e) {
      console.error('❌ Failed to toggle schedulable:', e);
      showToast('error', 'Failed to update schedulable status');
    }
  };


  const getTimeSinceJoined = (joinedDate) => {
    if (!joinedDate) return 'Unknown';
    const now = new Date();
    const joined = new Date(joinedDate);
    const diffTime = Math.abs(now - joined);
    // #5752: floor, not ceil (an hour-old join read "1 days ago"), and singular units.
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const ago = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;

    if (diffDays < 1) return 'Today';
    if (diffDays < 30) return ago(diffDays, 'day');
    if (diffDays < 365) return ago(Math.floor(diffDays / 30), 'month');
    return ago(Math.floor(diffDays / 365), 'year');
  };

  // OLD PTO Request Actions removed - using new PTO tab system

  // Employee Actions
  /**
   * Reset an employee's password to a new temporary one.
   *
   * THE GAP THIS CLOSES. "Add with Password" exists precisely for a worker with no reachable
   * email — a temp hand, someone whose auth email is a placeholder. The moment that person
   * forgets their password there was no way back: Supabase's reset flow emails a link to an
   * address nobody reads, and this panel offered only Edit and Deactivate. The manager who
   * created the login could not restore it.
   *
   * Same generator as Add with Password, and the same consequence: the new password must be
   * changed on next sign-in (the edge function sets must_change_password, which Login.js
   * already enforces). Authorisation is NOT decided here — the edge function re-derives the
   * caller's role server-side and refuses a target the caller may not outrank, so a manager
   * cannot reset an owner. This gate only avoids offering a button that would 403.
   */
  const handleResetEmployeePassword = async (employee) => {
    const name = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'this employee';
    if (!window.confirm(
      `Reset the password for ${name}?\n\n`
      + `Their current password stops working immediately. You will be shown a temporary `
      + `password to give them, and they must choose a new one when they next sign in.`
    )) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showToast('error', 'Your session expired — sign in again.');
        return;
      }

      const tempPassword = generateTempPassword();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ employee_id: employee.id, password: tempPassword })
      });

      const result = await response.json();

      if (!response.ok) {
        // The function's messages name the actual reason (no login, outranked, wrong company).
        showToast('error', result.error || 'Could not reset the password');
        return;
      }

      // Reuses the Add with Password card, which already has the copy button and the
      // select-all styling a manager needs to read a password out loud.
      setTempPasswordAlert({
        show: true,
        email: result.email || '',
        password: tempPassword,
        reset: true,
        name
      });

      if (result.warning) {
        showToast('warning', result.warning);
      } else {
        showToast('success', `Password reset for ${name}.`);
      }
    } catch (error) {
      console.error('Error resetting employee password:', error);
      showToast('error', `Could not reset the password: ${error.message}`);
    }
  };

  const handleDeactivateEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to deactivate this employee?')) return;

    try {
      // ONE WRITE: employees.status, with the result checked.
      //
      // This used to update `users` FIRST and treat that as the authoritative step, then log
      // an employees failure as a console warning and report success anyway. Both halves were
      // wrong. The users write could not succeed — the only UPDATE policy on that table is
      // `auth_user_id = auth.uid()` (verified live 2026-09-19), so updating somebody else's
      // row matches zero rows, which PostgREST reports with no error — and the employees
      // write, the one that actually landed, was the one whose failure was swallowed. That
      // combination is why 2 employees sat at employees.status='inactive' with
      // users.status='active' and kept their company access (board #1240).
      //
      // trg_mirror_employee_status_to_user now carries the status across in the database, so
      // the client writes the one column it is allowed to write and checks that it took.
      const { data: deactivated, error: empError } = await supabase
        .from('employees')
        .update({ status: 'inactive' })
        .eq('id', employeeId)
        .eq('company_id', user.company_id)
        .select('id');

      if (empError) throw empError;
      if (!deactivated || deactivated.length === 0) {
        throw new Error('Employee was not deactivated — no matching record. Refresh and try again.');
      }

      showToast('success', 'Employee deactivated successfully');
      queryClient.invalidateQueries({ queryKey: ['employees-list'] });
    } catch (error) {
      console.error('Error deactivating employee:', error);
      showToast('error', error.message || 'Failed to deactivate employee');
    }
  };

  /**
   * Board #2679. Deactivate existed; the only way BACK was to open the full Edit form,
   * change a status dropdown and save — which is a lot of ceremony for undoing a
   * one-click action, and easy to miss entirely.
   *
   * Mirrors handleDeactivateEmployee exactly, including the reasoning recorded there:
   * ONE write, to employees.status, with the result checked. The client must not write
   * users.status — the only UPDATE policy on that table is auth_user_id = auth.uid(), so
   * updating another employee's row matches zero rows and PostgREST reports no error.
   * trg_mirror_employee_status_to_user carries the status across in the database, which
   * is what restores company access atomically.
   *
   * The read-back is the "show success only after read-back confirms Active" the brief
   * asks for: .select('id, status') returns the row as it now stands, so a refused write
   * cannot report success.
   */
  const handleReactivateEmployee = async (employeeId) => {
    const ok = await confirm({
      title: 'Reactivate this employee?',
      message: 'They will be able to sign in again and will count as a billed seat.',
      confirmText: 'Reactivate',
      cancelText: 'Cancel',
      isDangerous: false
    });
    if (ok !== 'confirm') return;

    try {
      const { data: reactivated, error: empError } = await supabase
        .from('employees')
        .update({ status: 'active' })
        .eq('id', employeeId)
        .eq('company_id', user.company_id)
        .select('id, status');

      if (empError) throw empError;
      if (!reactivated || reactivated.length === 0) {
        throw new Error('Employee was not reactivated — no matching record. Refresh and try again.');
      }
      if (reactivated[0].status !== 'active') {
        throw new Error('The employee record did not come back as Active. Refresh and check.');
      }

      showToast('success', 'Employee reactivated');
      queryClient.invalidateQueries({ queryKey: ['employees-list'] });
    } catch (error) {
      console.error('Error reactivating employee:', error);
      showToast('error', error.message || 'Failed to reactivate employee');
    }
  };

  const handleResetPassword = async (employeeId, email) => {
    if (!window.confirm('Send password reset email to this employee?')) return;

    try {
      // TODO: Implement password reset email functionality
      showToast('info', 'Password reset email functionality will be implemented');
    } catch (error) {
      console.error('Error resetting password:', error);
      showToast('error', 'Failed to send password reset email');
    }
  };



const deleteEmployee = async (employeeId, employeeEmail) => {
    if (!employeeId) {
      showToast('error', 'Cannot delete: employee ID is missing');
      return;
    }

    if (!window.confirm(`Permanently delete ${employeeEmail || 'this employee'}?\n\nThis is only for removing someone added by mistake. If they have any work history you'll be asked to deactivate them instead.`)) {
      return;
    }

    try {
      // One server-side call does everything with the service-role key: it checks
      // for work history and BLOCKS the delete (steering to Deactivate) if any
      // exists, otherwise cleanly removes the employee + user + auth account.
      const { data, error } = await supabase.functions.invoke('admin-delete-employee', {
        body: { employeeId, companyId: user.company_id }
      });

      // A 409 (history) surfaces as a FunctionsHttpError; read the response body.
      if (error) {
        let payload = null;
        try { payload = await error.context?.json?.(); } catch (_) { /* non-JSON */ }
        if (payload?.reason === 'has_history') {
          showToast('error', payload.message || 'This employee has work history — deactivate them instead of deleting.');
          return;
        }
        throw new Error(payload?.error || error.message || 'Delete failed');
      }

      if (data?.blocked && data?.reason === 'has_history') {
        showToast('error', data.message || 'This employee has work history — deactivate them instead of deleting.');
        return;
      }

      showToast('success', `Employee ${employeeEmail || ''} deleted`.trim());
      queryClient.invalidateQueries({ queryKey: ['employees-list'] });
    } catch (error) {
      console.error('Error deleting employee:', error);
      showToast('error', `Failed to delete employee: ${error.message}`);
    }
  };

  const editEmployee = async (employee) => {
    console.log('📝 Editing employee:', employee);
    setEditingEmployee(employee);
    setFormData({
      email: employee.email || 'N/A',
      first_name: employee.first_name || '',
      middle_name: employee.middle_name || '',
      last_name: employee.last_name || '',
      phone_number: employee.phone || '',
      role: employee.role || 'technician',
      is_schedulable: employee.is_schedulable !== undefined ? employee.is_schedulable : true,
      // Compensation fields
      pay_type: employee.pay_type || 'hourly',
      hourly_rate: employee.hourly_rate || '',
      overtime_rate: employee.overtime_rate || '',
      annual_salary: employee.annual_salary ?? '',
      department: employee.department || '',
      capacity_hours_per_day: employee.capacity_hours_per_day || '8',
      job_title: employee.job_title || employee.role || '',
      // Additional fields
      employee_number: employee.employee_number || '',
      hire_date: employee.hire_date || '',
      termination_date: employee.termination_date || '',
      employee_type: employee.employee_type || 'full-time',
      emergency_contact_name: employee.emergency_contact_name || '',
      emergency_contact_phone: employee.emergency_contact_phone || '',
      emergency_contact_relationship: employee.emergency_contact_relationship || '',
      date_of_birth: employee.date_of_birth || '',
      pto_balance_hours: employee.pto_balance_hours || '0',
      status: employee.status || 'active',
      manager_id: employee.manager_id || '',
      certifications: employee.certifications || '',
      notes: employee.notes || ''
    });
    console.log('📝 Form data set - phone_number:', employee.phone, '(from employee.phone)');
    console.log('📝 Form data set - employee.user_id:', employee.user_id);

    // ✅ NEW: Load existing permissions from employee_permissions table
    try {
      console.log(`🔍 Fetching permissions for user_id=${employee.user_id}, company_id=${user.company_id}`);

      // Use Supabase client directly - it handles auth automatically
      const { data: permData, error: permError } = await supabase
        .from('employee_permissions')
        .select('*')
        .eq('user_id', employee.user_id)
        .eq('company_id', user.company_id);

      console.log(`🔍 Permissions fetch error:`, permError);
      console.log(`🔍 Permissions data from DB:`, permData);

      if (!permError && permData && permData.length > 0) {
        // Convert database columns to state format
        const dbPerms = permData[0];
        const loadedPerms = {};
        Object.keys(EMPLOYEE_MODULES).forEach(moduleKey => {
          const module = EMPLOYEE_MODULES[moduleKey];
          loadedPerms[module.key] = dbPerms[module.key] === true || dbPerms[module.key] === 'true' || dbPerms[module.key] === 't';
        });
        setEditingPermissions(loadedPerms);
        console.log('✅ Loaded permissions from DB:', loadedPerms);
      } else {
        // No permissions record exists or error, use defaults based on role
        console.log('⚠️ No permissions record found, using role defaults. Error:', permError?.message);
        setEditingPermissions(getDefaultPermissions(employee.role || 'technician'));
      }

      // "Can see all jobs": read it from the row when the column is there. With no row for
      // this employee yet, any company row shows whether the column exists.
      let seeAllSupported = false;
      let seeAll = false;
      const hasSeeAllColumn = (row) => !!row && Object.prototype.hasOwnProperty.call(row, 'can_see_all_jobs');
      if (!permError && permData && permData.length > 0) {
        seeAllSupported = hasSeeAllColumn(permData[0]);
        seeAll = seeAllSupported && permData[0].can_see_all_jobs === true;
      } else if (!permError) {
        const { data: anyRows, error: probeError } = await supabase
          .from('employee_permissions')
          .select('*')
          .eq('company_id', user.company_id)
          .limit(1);
        seeAllSupported = !probeError && hasSeeAllColumn(anyRows?.[0]);
      }
      setSeeAllJobsSupported(seeAllSupported);
      setEditingSeeAllJobs(seeAll);
    } catch (e) {
      console.warn('Failed loading employee permissions', e);
      setEditingPermissions(getDefaultPermissions(employee.role || 'technician'));
      setSeeAllJobsSupported(false);
      setEditingSeeAllJobs(false);
    }

    // Load employee row id and skills for editing
    try {
      const empRowRes = await fetch(`${SUPABASE_URL}/rest/v1/employees?user_id=eq.${employee.id}&company_id=eq.${user.company_id}&select=id`, {
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` }
      });
      if (empRowRes.ok) {
        const rows = await empRowRes.json();
        const row = rows && rows[0];
        if (row?.id) {
          setEditingEmployeeRowId(row.id);
          const esRes = await fetch(`${SUPABASE_URL}/rest/v1/employee_skills?employee_id=eq.${row.id}&select=skill_id,level`, {
            headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` }
          });
          if (esRes.ok) {
            const list = await esRes.json();
            const mapped = (list || []).map(r => ({ skill_id: r.skill_id, level: r.level ?? 1 }));
            setEditingEmployeeSkills(mapped);
            setOriginalEmployeeSkills(mapped);
          } else {
            setEditingEmployeeSkills([]);
            setOriginalEmployeeSkills([]);
          }
        }
      }
    } catch (e) {
      console.warn('Failed loading employee skills', e);
      setEditingEmployeeRowId(null);
      setEditingEmployeeSkills([]);
      setOriginalEmployeeSkills([]);
    }

    setShowEditForm(true);
  };

  const updateEmployee = async (e) => {
    e.preventDefault();

    const { email, first_name, middle_name, last_name, phone_number, role, is_schedulable, pay_type, hourly_rate, overtime_rate, department, capacity_hours_per_day, job_title, employee_number, hire_date, termination_date, employee_type, emergency_contact_name, emergency_contact_phone, emergency_contact_relationship, date_of_birth, pto_balance_hours, status, manager_id, certifications, notes } = formData;

    // Validation
    if (!email || !first_name || !last_name || !role) {
      showToast('error', 'Please fill in email, first name, last name, and role');
      return;
    }

    setFormLoading(true);

    try {
      // DEBUG: Log phone number handling
      console.log('🔍 PHONE DEBUG START');
      console.log('📱 formData.phone_number:', formData.phone_number);
      console.log('📱 Extracted phone_number:', phone_number);
      console.log('📱 editingEmployee:', editingEmployee);
      console.log('📱 editingEmployee.user_id:', editingEmployee?.user_id);

      // Build full_name from parts
      const fullName = [first_name, middle_name, last_name].filter(n => n && n.trim()).join(' ');

      // NOTE: User data (phone, email, role, name) is now updated via Edge Function below
      // This bypasses RLS which blocks admins from updating other users' records
      const userData = {
        email: email,
        phone: phone_number || null,
        role: role,
        first_name: first_name,
        last_name: last_name
      };

      console.log('📤 User data to update via Edge Function:', userData);

      // ✅ FIX: Get session token FIRST for proper RLS authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be logged in to update employee');
      }

      // Fix list 2026-10-24 #2: ask the edge function whether it will accept the role/email
      // BEFORE writing anything. It used to refuse only after the employees row and pay below
      // had already saved, leaving a half-saved employee behind an error toast.
      if (editingEmployee.user_id) {
        const checkResponse = await fetch(`${SUPABASE_URL}/functions/v1/admin-update-employee-permissions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: editingEmployee.user_id,
            company_id: user.company_id,
            user_data: userData,
            validate_only: true
          })
        });
        if (!checkResponse.ok) {
          const checkResult = await checkResponse.json().catch(() => ({}));
          throw new Error(checkResult.error || 'Failed to save employee data');
        }
      }

      // Update employees table with all fields (this is now the AUTHORITATIVE employee table)
      // NOTE: name fields are denormalized here for HR/operational purposes
      const employeeData = {
        // Core identity - from form
        first_name: first_name,
        last_name: last_name,
        // Schedulable and role
        is_schedulable: is_schedulable,
        job_title: job_title || role,
        // Compensation — same owner as the create path, so a salaried employee's derived cost
        // rate cannot be correct on create and silently revert to 0 on the first edit.
        ...(showCompensation ? resolveCompensation(formData) : withoutCompensation(resolveCompensation(formData))),
        capacity_hours_per_day: capacity_hours_per_day ? parseFloat(capacity_hours_per_day) : 8,
        // Organization
        department: department || null,
        employee_type: employee_type || 'full-time',
        // #5911: the owner reports to nobody (Settings → Org Structure pins them to the top).
        // This form still offered a Manager for an owner, which is how owners came to carry one.
        manager_id: String(role || '').toLowerCase() === 'owner' ? null : (manager_id || null),
        status: status || 'active',
        // Board #453: the create form's number was written straight through, so leaving it
        // blank produced an employee with NO number at all — which is why the live data has
        // rows the Employees list cannot order or reference. Allocate from the same counter
        // the invite and add-with-password edge functions use, so every route agrees.
        employee_number: employee_number || await nextEmployeeNumber(user.company_id),
        // Dates
        hire_date: hire_date || null,
        termination_date: termination_date || null,
        date_of_birth: date_of_birth || null,
        // Contact & Other
        emergency_contact_name: emergency_contact_name || null,
        emergency_contact_phone: emergency_contact_phone || null,
        emergency_contact_relationship: emergency_contact_relationship || null,
        pto_balance_hours: pto_balance_hours ? parseFloat(pto_balance_hours) : 0,
        // certifications is a text[] array in DB - convert comma-separated string to array
        certifications: certifications
          ? (Array.isArray(certifications) ? certifications : certifications.split(',').map(c => c.trim()).filter(c => c))
          : null,
        notes: notes || null
      };

      // hourly_rate / overtime_rate / annual_salary / date_of_birth / pto_balance_hours (and
      // SSN) now live in employee_compensation and are REVOKED on employees for app roles, so
      // a PATCH carrying any of them 42501s the whole row. Split them off here and upsert the
      // side table once the employees write has succeeded.
      const { employee: employeeRowData, comp: compensationData } = splitCompensationPayload(employeeData);

      console.log('📤 Employee data being sent to PATCH:', JSON.stringify(employeeRowData, null, 2));
      console.log('📤 Certifications type:', typeof employeeRowData.certifications, Array.isArray(employeeRowData.certifications) ? 'IS ARRAY' : 'NOT ARRAY');
      console.log('📤 Certifications value:', employeeRowData.certifications);

      const employeeResponse = await fetch(`${SUPABASE_URL}/rest/v1/employees?id=eq.${editingEmployee.id}&company_id=eq.${user.company_id}`, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,  // ✅ FIX: Use session token for RLS
          'Content-Type': 'application/json',
          // Board #5203. `return=representation` asks PostgREST to RETURN the whole row, i.e.
          // SELECT every column — and since the 2026-10-07 compensation split the six pay/PII
          // columns are REVOKED for app roles, so the PATCH itself was fine but its RETURNING
          // 42501'd: "permission denied for table employees" on every Edit Employee save, while
          // the Schedulable toggle (supabase-js, no representation) kept working. Nothing read
          // the body; the list reloads after the save.
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify(employeeRowData)
      });

      if (!employeeResponse.ok) {
        const empError = await employeeResponse.json();
        // Board #449. This was a console.warn and the function carried on to a green toast
        // and a closed modal, so a rejected write looked exactly like a saved one — the
        // employee's own record is the PRIMARY thing this form edits, and losing it silently
        // is the worst outcome available. Throwing keeps the modal open with the entered
        // values, which is what lets the user retry without retyping.
        throw new Error(empError.message || 'Failed to update employee details');
      } else {
        console.log(`✅ Employee details updated successfully`);
      }

      // Same "throw, keep the modal open" handling as the employees write: a rejected pay
      // rate must not look like a saved one.
      const { error: compError } = await upsertEmployeeCompensation(
        supabase, editingEmployee.id, user.company_id, compensationData
      );
      if (compError) {
        throw new Error(compError.message || 'Failed to update employee compensation');
      }

      // Save user data AND permissions using Edge Function (bypasses RLS issues)
      // RLS policy on users table only allows users to update their OWN record
      // So admins editing other employees need to use the service role via Edge Function
      try {
        const permColumns = Object.values(EMPLOYEE_MODULES).map(m => m.key);
        const cappedPermissions = withSeatCap(editingPermissions, formData.role);
        const permData = {};
        permColumns.forEach(key => {
          permData[key] = cappedPermissions[key] === true;
        });
        // Only crew seats are limited to their assigned jobs; any other role saves false so a
        // later move back to a crew role starts from the default (assigned jobs only).
        if (seeAllJobsSupported) {
          permData.can_see_all_jobs = isCrewSeatRole(formData.role) && editingSeeAllJobs === true;
        }

        console.log('🔐 Calling admin-update-employee-permissions Edge Function...');
        console.log('📱 Including user_data:', userData);
        console.log('🔑 Permissions being sent:', permData);

        const permResponse = await fetch(`${SUPABASE_URL}/functions/v1/admin-update-employee-permissions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: editingEmployee.user_id,
            company_id: user.company_id,
            permissions: permData,
            user_data: userData  // ✅ NEW: Include user data (phone, email, role)
          })
        });

        const permResult = await permResponse.json();

        if (!permResponse.ok) {
          console.error('❌ Edge Function error:', permResult);
          throw new Error(permResult.error || 'Failed to save employee data');
        }

        // The function returns HTTP 200 even when the nested write failed, so `ok` alone is
        // not an answer — role and phone live in this payload, and a 200 carrying
        // user_data_result.success === false was reported to the user as a successful save
        // (board #449).
        if (permResult?.user_data_result && permResult.user_data_result.success === false) {
          throw new Error(permResult.user_data_result.error || 'Role and contact details were not saved');
        }

        // READ IT BACK. Board #449's definitive repro was three consecutive saves that
        // reported success while users.role stayed 'lead_technician', on a bundle that already
        // contained every error check above — so the checks were necessary and not sufficient.
        // A 200 carrying success:true is the edge function's opinion about its own write; this
        // is the row. It costs one select and it is the only thing that can distinguish "saved"
        // from "reported saved", which is the entire finding.
        if (editingEmployee.user_id) {
          const { data: savedUser, error: readBackError } = await supabase
            .from('users')
            .select('role, phone')
            .eq('id', editingEmployee.user_id)
            .maybeSingle();

          // A read-back that cannot run is NOT a pass. Saying so beats inventing confidence.
          if (readBackError) {
            throw new Error(`Saved, but the change could not be confirmed: ${readBackError.message}. Refresh to check.`);
          }
          if (!savedUser) {
            throw new Error('Saved, but the employee record could not be re-read. Refresh to check.');
          }
          if (savedUser.role !== role) {
            throw new Error(`Role did not save — it is still ${savedUser.role}. Try again.`);
          }
        }

        console.log(`✅ Employee data and permissions saved successfully:`, permResult);
      } catch (permError) {
        // Re-thrown rather than downgraded: this call carries role, phone and permissions.
        console.error('❌ Error saving employee data:', permError.message);
        throw permError;
      }

      // ✅ FIX: Save employee skills to database
      let skillsSaveFailed = null;
      try {
        console.log('🔧 Saving employee skills...', editingEmployeeSkills);
        
        // DATA LOSS if the insert fails: this deletes every existing skill row
        // first, so a rejected insert leaves the employee with NONE. It did
        // exactly that — the payload carried `verified`, a column
        // employee_skills does not have (live columns: employee_id, skill_id,
        // level, created_at, updated_at, company_id), so every insert 400'd and
        // the error was downgraded to console.warn behind a success toast.
        // Same shape as LICS-LOSS-1 (top Save Changes wiping companies.licenses).
        const { error: deleteError } = await supabase
          .from('employee_skills').delete().eq('employee_id', editingEmployee.id);
        if (deleteError) throw deleteError;

        // Then insert new skills
        if (editingEmployeeSkills && editingEmployeeSkills.length > 0) {
          const skillsToInsert = editingEmployeeSkills.map(skill => ({
            employee_id: editingEmployee.id,
            skill_id: skill.skill_id,
            level: skill.level || 1,
            // Required for the company-scoped RLS policy; it was missing too,
            // so this would have been rejected even with the column names right.
            company_id: editingEmployee.company_id || user?.company_id
          }));

          const { error: skillsError } = await supabase.from('employee_skills').insert(skillsToInsert);
          // Must NOT be swallowed: the delete above already ran, so a silent
          // failure here is unrecoverable skill loss the user is never told about.
          if (skillsError) throw skillsError;
          console.log('✅ Employee skills saved successfully');
        } else {
          console.log('ℹ️ No skills to save (empty array)');
        }
      } catch (skillsError) {
        // The employee record itself saved, so we do NOT discard that. But the
        // delete above already ran, so skills may now be GONE — the user has to
        // be told, or they lose data behind a green toast (the bug this fixes).
        console.error('⚠️ Error saving employee skills:', skillsError.message);
        skillsSaveFailed = skillsError.message;
      }

      if (skillsSaveFailed) {
        showToast('error', `Employee saved, but skills did NOT save (${skillsSaveFailed}). Re-add them and try again.`);
      } else {
        showToast('success', `✅ Employee updated successfully!`);
      }
      setFormData({
        email: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        phone_number: '',
        role: 'employee',
        is_schedulable: true,
        pay_type: 'hourly',
        hourly_rate: '',
        overtime_rate: '',
        annual_salary: '',
        department: '',
        capacity_hours_per_day: '8',
        job_title: '',
        employee_number: '',
        hire_date: '',
        termination_date: '',
        employee_type: 'full-time',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',
        date_of_birth: '',
        pto_balance_hours: '0',
        status: 'active',
        manager_id: '',
        certifications: '',
        notes: ''
      });
      setEditingPermissions(getDefaultPermissions('technician'));
      setShowEditForm(false);
      setEditingEmployee(null);
      queryClient.invalidateQueries({ queryKey: ['employees-list'] });

    } catch (error) {
      console.error('Error updating employee:', error);
      showToast('error', error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditPermissionToggle = (permission) => {
    if (isSeatLockedModule(permission, formData.role)) return;
    console.log(`🔄 Toggling permission: ${permission}`);
    setEditingPermissions(prev => {
      const newValue = !prev[permission];
      console.log(`🔄 ${permission}: ${prev[permission]} → ${newValue}`);
      return {
        ...prev,
        [permission]: newValue
      };
    });
  };

  return (
    <div className="space-y-8 fade-in">
      {/* ✅ MODERNIZED: Glassmorphic Page Header with Action Buttons - Mobile Responsive */}
      <div className="bg-gradient-to-br from-white/80 to-indigo-50/40 dark:from-slate-800/80 dark:to-slate-900/40 backdrop-blur-xl rounded-3xl border border-indigo-200/50 dark:border-indigo-900/50 shadow-2xl shadow-indigo-500/5 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Title section */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-3 sm:p-4 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl sm:rounded-2xl shadow-lg shadow-indigo-500/30 flex-shrink-0">
              <UserGroupIcon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white truncate">{activeTab === 'pto-management' ? 'Time Off' : 'Team Members'}</h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-0.5 sm:mt-1 hidden sm:block">Manage your team, track performance, and handle time off requests</p>
            </div>
          </div>

          {/* ✅ MODERNIZED: Glassmorphic Action Buttons - Responsive Grid */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Export - icon only on mobile */}
            <button
              onClick={() => exportEmployeesCSV(employees)}
              className="group relative p-2.5 sm:px-4 sm:py-2.5 bg-gradient-to-br from-indigo-500 to-indigo-600 backdrop-blur-xl rounded-xl border border-indigo-400/50 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all duration-300 hover:scale-105"
              title="Export"
              aria-label="Export employees"
            >
              <div className="flex items-center gap-2">
                <ArrowDownTrayIcon className="w-5 h-5 text-white" />
                <span className="hidden sm:inline text-sm font-medium text-white">Export</span>
              </div>
            </button>

            {/* Import - icon only on mobile */}
            <button
              onClick={() => setShowImportModal(true)}
              className="group relative p-2.5 sm:px-4 sm:py-2.5 bg-gradient-to-br from-indigo-500 to-indigo-600 backdrop-blur-xl rounded-xl border border-indigo-400/50 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all duration-300 hover:scale-105"
              title="Import"
              aria-label="Import employees"
            >
              <div className="flex items-center gap-2">
                <ArrowUpTrayIcon className="w-5 h-5 text-white" />
                <span className="hidden sm:inline text-sm font-medium text-white">Import</span>
              </div>
            </button>

            {/* Invite by Email (PRIMARY) — the employee self-onboards and sets their own
                password (admin-invite-user edge fn). Always shows text.

                These two buttons were "Add Employee" and "Add employee manually", which name
                the same action twice and leave the difference — whether an email goes out to
                a real person — in a tooltip nobody hovers. A new admin wanting to add a
                contractor had to guess, and guessing wrong either emails someone unexpectedly
                or creates an account nobody is told about. The labels now say which channel
                each one uses, since that is the whole difference between them. */}
            <button
              onClick={() => setShowInviteModal(true)}
              className="group relative px-3 py-2.5 sm:px-4 bg-gradient-to-br from-indigo-500 to-indigo-600 backdrop-blur-xl rounded-xl border border-indigo-400/50 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all duration-300 hover:scale-105"
              title="Emails them an invite link; they set their own password"
            >
              <div className="flex items-center gap-1.5 sm:gap-2">
                <EnvelopeIcon className="w-5 h-5 text-white" />
                <span className="text-sm font-medium text-white">Invite by Email</span>
              </div>
            </button>

            {/* Add with Password (SECONDARY) — creates the login with a temp password the owner
                shares (admin-create-user edge fn). Email is still required (auth.users needs
                it), so this is NOT "add without an email address" — it is "no email is sent". */}
            {/* Board #1773: at 400px the label is hidden and this was a bare PlusIcon —
                the same glyph as the page's floating create action, in a toolbar, with the
                only explanation in a tooltip a touch device never shows. A key says
                "credentials", which is exactly what distinguishes this from Invite, and the
                aria-label names the ACTION rather than repeating the tooltip's prose. */}
            <button
              onClick={() => {
                if (editingEmployee) { setEditingEmployee(null); setFormData(NEW_EMPLOYEE_FORM); }
                setShowCreateForm(true);
              }}
              className="group relative p-2.5 sm:px-4 sm:py-2.5 rounded-xl border border-indigo-400/40 text-indigo-700 dark:text-indigo-100 hover:bg-indigo-500/10 backdrop-blur-xl transition-all duration-300"
              title="No email is sent — you get a temporary password to give them yourself"
              aria-label="Add with Password"
            >
              <div className="flex items-center gap-2">
                <KeyIcon className="w-5 h-5" />
                <span className="hidden sm:inline text-sm font-medium">Add with Password</span>
              </div>
            </button>

            {/* Board #951. A "Settings" button sat here in the same bright primary treatment
                as Invite and Add, and went to undifferentiated global Settings — a
                destination already one click away in the sidebar, identifying no
                employee-specific configuration. It competed for attention with the two
                actions people come to this page to perform. Removed rather than demoted:
                there is nothing here it was a shortcut TO. If an employee-specific settings
                surface appears later, the right control is a named deep link to that surface,
                not a generic duplicate of a nav item. */}
          </div>
        </div>
      </div>

      {/* ✅ MODERNIZED: Glassmorphic Stats Cards */}
      <EmployeesStats
        analytics={analytics}
        setStatusFilter={setStatusFilter}
        setActiveTab={setActiveTab}
        statusFilter={statusFilter}
        activeTab={activeTab}
      />

      {/* ✅ MODERNIZED: Glassmorphic Tab Navigation */}
      <div className="bg-gradient-to-br from-white/80 to-gray-50/40 dark:from-slate-800/80 dark:to-slate-900/40 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-700/50 shadow-2xl shadow-gray-500/5 p-2">
        <nav className="flex space-x-2">
          <button
            onClick={() => setActiveTab('employees')}
            className={`flex-1 py-3 px-4 rounded-2xl font-medium text-sm transition-all duration-300 ${
              activeTab === 'employees'
                ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                : 'text-gray-600 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-slate-700/50 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <UsersIcon className="w-4 h-4" />
              Team Members
            </div>
          </button>

          {/* Admin-only PTO tabs */}
          {isOwnerOrAdmin(user) && (
            <>
              <button
                onClick={() => setActiveTab('pto-management')}
                className={`flex-1 py-3 px-4 rounded-2xl font-medium text-sm transition-all duration-300 ${
                  activeTab === 'pto-management'
                    ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/30'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-slate-700/50 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <ClockIcon className="w-4 h-4" />
                  Time Off
                </div>
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Alert */}
      {alert.show && (
        <div className={`p-4 rounded-md ${
          alert.type === 'success' ? 'bg-green-100 text-green-700' :
          alert.type === 'warning' ? 'bg-yellow-100 text-yellow-700' :
          alert.type === 'info' ? 'bg-blue-100 text-blue-700' :
          'bg-red-100 text-red-700'
        }`}>
          <pre className="whitespace-pre-wrap font-sans">{alert.message}</pre>
        </div>
      )}

      {/* Temp Password Alert */}
      {/* This panel was UNREADABLE in dark mode - dark green on dark navy. Root cause is
          index.css:102, a global `.dark .bg-white { background-color: <dark> !important }`. The
          card was written light-only: `bg-green-50` has no dark variant so it stayed pale, while
          the two `bg-white` value boxes inside it were forced dark by that rule, leaving
          `text-green-900` on a near-black background.

          The global rule is deliberately NOT touched - it applies to the whole app. This panel
          states both modes instead. The value boxes use `bg-white/95`, a DIFFERENT class string
          from `bg-white`, so the !important selector cannot reach it; that slash-opacity form is
          already the idiom the rest of this codebase uses (modern-enhancements.css). Worth being
          careful here specifically: the temp password is shown exactly once and cannot be
          recovered afterwards, so illegible is the same as lost. */}
      {tempPasswordAlert.show && (
        <div className="bg-green-50 dark:bg-green-950/50 border-2 border-green-500 dark:border-green-600 rounded-lg p-6 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              {/* The same card now reports two different actions, so it says which one.
                  Headed "Created" after a reset would be wrong twice over: no employee was
                  created, and it hides that an existing password has just stopped working. */}
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
                {tempPasswordAlert.reset
                  ? `🔑 Password reset${tempPasswordAlert.name ? ` for ${tempPasswordAlert.name}` : ''}`
                  : '✅ Employee Created Successfully!'}
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">📧 Email:</p>
                  <p className="text-base text-green-900 dark:text-green-50 font-mono bg-white/95 dark:bg-slate-900/70 px-3 py-2 rounded border border-green-200 dark:border-green-800">{tempPasswordAlert.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">🔑 Temporary Password:</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl text-green-900 dark:text-green-50 font-mono bg-white/95 dark:bg-slate-900/70 px-3 py-2 rounded border border-green-200 dark:border-green-800 flex-1 select-all">{tempPasswordAlert.password}</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(tempPasswordAlert.password);
                        showToast('success', '✅ Password copied to clipboard!', 2000);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800/60 rounded-lg p-3 mt-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>⚠️ IMPORTANT:</strong> Copy this password and give it to the employee. They can login immediately and should change it on first login. This alert will stay open until you close it.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setTempPasswordAlert({ show: false, email: '', password: '', reset: false, name: '' })}
              className="flex-shrink-0 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Analytics summary tiles are rendered once by <EmployeesStats> above.
          The duplicate second row was removed (audit U1, 2026-07-14). */}



      {/* Create Employee Modal - Glassmorphic Dark Theme */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto">
          <div
            ref={createTrapRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-employee-title"
            className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h3 id="add-employee-title" className="text-xl font-semibold">Add New Employee</h3>
                <p className="text-indigo-200 text-sm">Set up employee details and permissions</p>
                {/* Board #4284: say up front what Create will do with the login. */}
                <p className="text-indigo-100 text-sm mt-1">No email is sent. A one-time temporary password appears after you click Create — give it to them yourself.</p>
              </div>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-indigo-200 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={createEmployee} className="flex-1 flex flex-col">
              {/* Main Content Area */}
              <div className="flex-1 p-6 space-y-8">

                {/* Top Half - Contact Information */}
                <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 shadow-xl">
                  <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                        placeholder="employee@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                        placeholder="John"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Middle Name
                      </label>
                      <input
                        type="text"
                        value={formData.middle_name}
                        onChange={(e) => setFormData({...formData, middle_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                        placeholder="Robert (optional)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                        placeholder="Smith"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Phone Number
                      </label>
                      <PhoneField
                        label=""
                        name="phone_number"
                        testId="employee-phone-number"
                        value={formData.phone_number || ''}
                        onChange={(value) => setFormData({...formData, phone_number: value})}
                        labelClassName="hidden"
                        inputClassName="flex-1 min-w-0 w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Role *
                      </label>
                      <select
                        required
                        value={formData.role}
                        onChange={(e) => {
                          const newRole = e.target.value;
                          setFormData({...formData, role: newRole});
                          // ✅ Auto-update permissions based on role
                          setNewEmployeePermissions(getDefaultPermissions(newRole));
                        }}
                        className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      >
                        {getAvailableRoles().map(role => (
                          <option key={role.value} value={role.value} className="bg-slate-800">
                            {role.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-slate-400">
                        Permissions will be automatically set based on role (you can customize them below)
                      </p>
                    </div>

                    {/* ✅ NEW: Schedulable Toggle */}
                    <div className="md:col-span-2">
                      <div className="bg-indigo-900/30 border border-indigo-700/50 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.is_schedulable}
                              onChange={(e) => setFormData({...formData, is_schedulable: e.target.checked})}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                          </label>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-white flex items-center gap-2">
                              <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Available for Scheduling
                            </div>
                            <div className="text-xs text-slate-300 mt-1">
                              {formData.is_schedulable ? (
                                <span className="text-green-400">✅ This employee will appear in scheduling dropdowns and calendar</span>
                              ) : (
                                <span className="text-orange-400">⚠️ This employee will NOT appear in scheduling (office staff, admin, etc.)</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 mt-2">
                              <strong className="text-slate-300">Enable for:</strong> Field technicians, installers, owner-operators<br/>
                              <strong className="text-slate-300">Disable for:</strong> Office managers, bookkeepers, dispatchers, admin staff
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Middle Half - Compensation & Employment Details */}
                <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 shadow-xl">
                  <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Compensation & Employment
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Board #451. Pay rates were rendered to anyone with the broad `employees`
                        module, which manager and supervisor both get by default — so whoever
                        could fix a phone number could also read and change every rate in the
                        company. Gated on the EXISTING `payroll` permission (owners and admins
                        always, since they are who grant it). Employment fields below stay
                        visible: schedulability and capacity are not pay. */}
                    {!showCompensation && (
                      <div className="md:col-span-2 rounded-lg border border-slate-700/60 bg-slate-800/40 p-4">
                        <p className="text-sm text-slate-300">
                          Pay details are hidden. They need the <span className="font-medium">Payroll</span> permission.
                        </p>
                      </div>
                    )}

                    {showCompensation && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Pay Type
                      </label>
                      {/* Board #452: Piece Rate is gone. It needs a units-and-amount model that
                          does not exist, and offering it stored an hourly number under a
                          piece-rate label. Live 2026-09-17, 0 employees had ever been saved as
                          salary or piece_rate, so nothing was stranded by removing it. */}
                      <select
                        value={formData.pay_type || 'hourly'}
                        onChange={(e) => setFormData({...formData, pay_type: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      >
                        {PAY_TYPES.map((pt) => (
                          <option key={pt.value} value={pt.value} className="bg-slate-800">{pt.label}</option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-400 mt-1">
                        Used to cost jobs from tracked time. This is not payroll.
                      </p>
                    </div>
                    )}

                    {/* Only the fields that apply to the chosen type are rendered, so the form
                        can no longer ask a salaried employee for an hourly rate. */}
                    {showCompensation && ((formData.pay_type || 'hourly') === 'salary' ? (
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Annual Salary ($)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.annual_salary || ''}
                          onChange={(e) => setFormData({...formData, annual_salary: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                          placeholder="0.00"
                        />
                        {/* The derived rate is SHOWN, with its divisor, because a job cost
                            computed from a number the user never saw is one they cannot check. */}
                        <p className="text-xs text-slate-400 mt-1">
                          {hourlyCostFromSalary(formData.annual_salary) > 0
                            ? `Costs jobs at $${roundRate(hourlyCostFromSalary(formData.annual_salary)).toFixed(2)}/hr (salary ÷ ${STANDARD_ANNUAL_HOURS} standard hours).`
                            : `Job cost per hour is derived as salary ÷ ${STANDARD_ANNUAL_HOURS} standard hours.`}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Hourly Rate ($)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.hourly_rate || ''}
                            onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                            className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                            placeholder="0.00"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Overtime Rate ($)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.overtime_rate || ''}
                            onChange={(e) => setFormData({...formData, overtime_rate: e.target.value})}
                            className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                            placeholder="0.00"
                          />
                        </div>
                      </>
                    ))}

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Department
                      </label>
                      <input
                        type="text"
                        value={formData.department || ''}
                        onChange={(e) => setFormData({...formData, department: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                        placeholder="e.g., Service, Installation, Office"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Capacity (Hours/Day)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        step="0.5"
                        value={formData.capacity_hours_per_day || '8'}
                        onChange={(e) => setFormData({...formData, capacity_hours_per_day: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                        placeholder="8"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Job Title
                      </label>
                      <input
                        type="text"
                        value={formData.job_title || ''}
                        onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                        placeholder="e.g., Technician, Project Manager"
                      />
                    </div>
                  </div>
                </div>

                {/* Bottom Half - Module Access */}
                <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 shadow-xl">
                  <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Module Access
                  </h4>

                  {/* ✅ NEW: Organized by category */}
                  <div className="space-y-6">
                    {Object.entries({
                      Core: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Core'),
                      Sales: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Sales'),
                      HR: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'HR'),
                      Finance: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Finance'),
                      Operations: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Operations'),
                      Admin: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Admin')
                    }).map(([category, modules]) => (
                      <div key={category}>
                        <h5 className="text-sm font-semibold text-slate-300 mb-3">{category}</h5>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                          {modules.map(module => {
                            const seatLocked = isSeatLockedModule(module.key, formData.role);
                            return (
                            <div key={module.key} className={`flex items-center gap-3 p-3 backdrop-blur-xl rounded-lg border transition ${seatLocked ? 'bg-slate-800/40 border-slate-700/50 opacity-70' : 'bg-slate-700/40 border-slate-600/50 hover:bg-slate-700/60'}`}>
                              <label className={`relative inline-flex items-center ${seatLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                <input
                                  type="checkbox"
                                  checked={!seatLocked && (newEmployeePermissions[module.key] || false)}
                                  onChange={() => handlePermissionToggle(module.key)}
                                  disabled={seatLocked}
                                  className="sr-only peer"
                                />
                                <div className="w-10 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50"></div>
                              </label>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-white">{module.label}</div>
                                <div className="text-xs text-slate-400">
                                  {seatLocked ? 'Needs a Full Access seat — change the role to grant this' : module.description}
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
              </div>

              {/* Bottom Action Bar */}
              <div className="border-t border-slate-700/50 bg-slate-800/80 backdrop-blur-xl px-6 py-4 flex justify-between items-center">
                <div className="text-sm text-slate-400">
                  Fill in employee details and set their permissions
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2.5 bg-slate-700/60 hover:bg-slate-600/60 text-slate-300 rounded-xl border border-slate-600/50 font-medium transition"
                    disabled={formLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white rounded-xl font-medium shadow-lg shadow-indigo-500/30 transition"
                    disabled={formLoading}
                  >
                    {formLoading ? 'Creating Employee...' : 'Create Employee'}
                  </button>
                </div>
              </div>
            </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditForm && editingEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={editTrapRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-employee-title"
            className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <h3 id="edit-employee-title" className="text-lg font-semibold mb-4">Edit Employee</h3>

            <form onSubmit={updateEmployee}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="employee@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="John"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Phone Number
                  </label>
                  <PhoneField
                    label=""
                    name="phone_number"
                    testId="employee-phone-number"
                    value={formData.phone_number || ''}
                    onChange={(value) => setFormData({...formData, phone_number: value})}
                    labelClassName="hidden"
                    inputClassName="flex-1 min-w-0 w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Role *
                  </label>
                  <select
                    required
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {getAvailableRoles().map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ✅ NEW: Schedulable Toggle */}
                {/* Board #448, second pass — the panel Jerry screenshotted on 2026-09-18.
                    The first pass gave the CONTAINER and the title a dark variant and stopped
                    there, so what was left read as a white-out: the two status lines, the
                    Enable/Disable guidance and the toggle track had no dark variant at all,
                    and `text-gray-500` on a dark panel is the faint grey in the screenshot.

                    The background was also the #437 defect — `dark:bg-blue-900/25` is a 25%
                    tint, and a quarter-strength colour over a translucent modal composites to
                    something neither light nor dark, which is why the box looked washed out
                    rather than simply wrong. Solid colour, no opacity. */}
                <div className="bg-blue-50 dark:bg-slate-800 border border-blue-200 dark:border-slate-600 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_schedulable}
                        onChange={(e) => setFormData({...formData, is_schedulable: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-500/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Available for Scheduling
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                        {formData.is_schedulable ? (
                          <span className="text-green-700 dark:text-green-300">✅ This employee will appear in scheduling dropdowns and calendar</span>
                        ) : (
                          <span className="text-orange-700 dark:text-orange-300">⚠️ This employee will NOT appear in scheduling (office staff, admin, etc.)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-slate-300 mt-2">
                        <strong>Enable for:</strong> Field technicians, installers, owner-operators<br/>
                        <strong>Disable for:</strong> Office managers, bookkeepers, dispatchers, admin staff
                      </div>
                    </div>
                  </div>
                </div>

                {/* Compensation Section */}
                <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Compensation</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Board #452 SECOND PASS. This is the OTHER compensation form in this file
                        and the first fix missed it completely — Jerry's verification caught
                        exactly that: Piece Rate was still offered here, and Salary still showed
                        Hourly and Overtime. Board #451's payroll gate missed this form too, for
                        the same reason: I fixed the section I had opened and assumed it was the
                        only one. Same owner and same rules as the other form now. */}
                    {!showCompensation && (
                      <div className="md:col-span-2 rounded-lg border border-slate-200 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-800/40 p-3">
                        <p className="text-sm text-gray-600 dark:text-slate-300">
                          Pay details are hidden. They need the <span className="font-medium">Payroll</span> permission.
                        </p>
                      </div>
                    )}

                    {showCompensation && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Pay Type
                      </label>
                      <select
                        value={formData.pay_type || 'hourly'}
                        onChange={(e) => setFormData({...formData, pay_type: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {PAY_TYPES.map((pt) => (
                          <option key={pt.value} value={pt.value}>{pt.label}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                        Used to cost jobs from tracked time. This is not payroll.
                      </p>
                    </div>
                    )}

                    {showCompensation && ((formData.pay_type || 'hourly') === 'salary' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                          Annual Salary
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-slate-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.annual_salary || ''}
                            onChange={(e) => setFormData({...formData, annual_salary: e.target.value})}
                            className="w-full pl-7 pr-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          {hourlyCostFromSalary(formData.annual_salary) > 0
                            ? `Costs jobs at $${roundRate(hourlyCostFromSalary(formData.annual_salary)).toFixed(2)}/hr (salary ÷ ${STANDARD_ANNUAL_HOURS} standard hours).`
                            : `Job cost per hour is derived as salary ÷ ${STANDARD_ANNUAL_HOURS} standard hours.`}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                            Hourly Rate
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-slate-400">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.hourly_rate}
                              onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                              className="w-full pl-7 pr-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                            Overtime Rate
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-slate-400">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.overtime_rate}
                              onChange={(e) => setFormData({...formData, overtime_rate: e.target.value})}
                              className="w-full pl-7 pr-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </>
                    ))}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Daily Capacity (Hours)
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={formData.capacity_hours_per_day}
                        onChange={(e) => setFormData({...formData, capacity_hours_per_day: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="8"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Department
                      </label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => setFormData({...formData, department: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g., Service, Installation"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Job Title
                      </label>
                      <input
                        type="text"
                        value={formData.job_title}
                        onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g., Senior Technician"
                      />
                    </div>
                  </div>
                </div>

                {/* Employment Details Section */}
                <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Employment Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Employee Number
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.employee_number}
                          onChange={(e) => setFormData({...formData, employee_number: e.target.value})}
                          className="flex-1 px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="EMP-001"
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            // Board #453. This READ companies.employee_number_next and never
                            // advanced it, so every click returned the same number — and on
                            // Jerry's company that number (11) already belonged to another
                            // employee. employees carries UNIQUE(company_id, employee_number),
                            // so saving it was rejected and the employee kept their old one.
                            //
                            // generate_document_number is the row-locked allocator the other
                            // document types use: it LOCKS the counter, increments it and
                            // returns the number, so two clicks (or two people) can never be
                            // handed the same one. It also reads
                            // company_settings.next_employee_number rather than the second
                            // counter on companies — see the note below about the two.
                            const { data: generated, error: genError } = await supabase
                              .rpc('generate_document_number', {
                                p_company_id: user.company_id,
                                p_doc_type: 'employee',
                              });
                            if (genError || !generated) {
                              showToast('error', genError?.message || 'Could not generate an employee number');
                              return;
                            }
                            setFormData({ ...formData, employee_number: generated });
                          }}
                          className="px-3 py-2 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                          title="Generate next employee number"
                        >
                          Generate
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Click Generate to reserve the next number, or type your own.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Employee Type
                      </label>
                      <select
                        value={formData.employee_type}
                        onChange={(e) => setFormData({...formData, employee_type: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="full-time">Full-time</option>
                        <option value="part-time">Part-time</option>
                        <option value="contractor">Contractor</option>
                        <option value="seasonal">Seasonal</option>
                        <option value="intern">Intern</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Hire Date
                      </label>
                      <input
                        type="date"
                        value={formData.hire_date}
                        onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Termination Date
                      </label>
                      <input
                        type="date"
                        value={formData.termination_date}
                        onChange={(e) => setFormData({...formData, termination_date: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Leave empty if still employed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {/* pending_invite is a REAL stored status — 33 employees carry it
                            live — but it was not an option here. A <select> whose value
                            matches no option falls back to showing the first one, so every
                            invited employee's edit modal read "Active"
                            (F-EMP-2026-08-31-2340-3).

                            Rendered only when it IS the current value, and disabled: it is a
                            state the invite flow owns (set on invite, cleared when the
                            employee accepts), not something an owner should assign by hand.
                            Showing it truthfully without making it selectable is the honest
                            middle. The other enum values (probation, suspended, retired)
                            stay off the list deliberately — this is the existing offer, not
                            a product change. */}
                        {formData.status === 'pending_invite' && (
                          <option value="pending_invite" disabled>Pending Invite — awaiting sign-up</option>
                        )}
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="terminated">Terminated</option>
                        <option value="on_leave">On Leave</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Manager
                      </label>
                      {String(formData.role || '').toLowerCase() === 'owner' ? (
                        <p className="px-3 py-2 text-sm text-gray-500 dark:text-white/50">
                          Owner — top of the organization
                        </p>
                      ) : (
                      <select
                        value={formData.manager_id}
                        onChange={(e) => setFormData({...formData, manager_id: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">None</option>
                        {employees.filter(emp => emp.id !== editingEmployee?.id).map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.full_name || `${emp.first_name} ${emp.last_name}`}
                          </option>
                        ))}
                      </select>
                      )}
                    </div>
                  </div>
                </div>

                {/* Personal & Emergency Contact Section */}
                <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Personal & Emergency Contact</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Emergency Contact Name
                      </label>
                      <input
                        type="text"
                        value={formData.emergency_contact_name}
                        onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g., Jane Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Emergency Contact Phone
                      </label>
                      <PhoneField
                        label=""
                        name="emergency_contact_phone"
                        testId="employee-emergency-contact-phone"
                        value={formData.emergency_contact_phone || ''}
                        onChange={(value) => setFormData({...formData, emergency_contact_phone: value})}
                        labelClassName="hidden"
                        inputClassName="flex-1 min-w-0 w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Emergency Contact Relationship
                      </label>
                      <input
                        type="text"
                        value={formData.emergency_contact_relationship}
                        onChange={(e) => setFormData({...formData, emergency_contact_relationship: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g., Spouse, Parent, Sibling"
                      />
                    </div>
                  </div>
                </div>

                {/* Certifications & Notes Section */}
                <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Certifications & Notes</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Certifications
                      </label>
                      <textarea
                        value={formData.certifications}
                        onChange={(e) => setFormData({...formData, certifications: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="OSHA 30, EPA 608, Class A CDL"
                        rows={3}
                      />
                      <p className="text-xs text-gray-500 mt-1">Separate multiple certifications with commas</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Additional notes about this employee..."
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                {/* Skills (Phase 1) */}
                <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Skills</h4>
                  <SkillsPicker
                    value={editingEmployeeSkills}
                    onChange={setEditingEmployeeSkills}
                    showLevel={true}
                    showQuantity={false}
                    allowCreate={true}
                  />
                  <div className="text-xs text-gray-500 mt-2">Skills are used by Smart Scheduling to match required job skills and levels</div>
                </div>


                {/* ✅ NEW: Module Access - Organized by category */}
                <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Module Access</h4>
                  {seeAllJobsSupported && isCrewSeatRole(formData.role) && (
                    <div className="flex items-start gap-3 p-2 mb-3 rounded-lg bg-gray-50" data-testid="employee-see-all-jobs">
                      <label className="relative inline-flex items-center mt-0.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingSeeAllJobs}
                          onChange={() => setEditingSeeAllJobs(prev => !prev)}
                          className="sr-only peer"
                          data-testid="employee-see-all-jobs-toggle"
                          aria-label="Can see all jobs"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">Can see all jobs</div>
                        <div className="text-xs text-gray-500">Off: this technician sees only the jobs assigned to them.</div>
                      </div>
                    </div>
                  )}
                  <div className="space-y-4 max-h-80 overflow-y-auto">
                    {Object.entries({
                      Core: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Core'),
                      Sales: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Sales'),
                      HR: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'HR'),
                      Finance: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Finance'),
                      Operations: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Operations'),
                      Admin: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Admin')
                    }).map(([category, modules]) => (
                      <div key={category}>
                        <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">{category}</h5>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                          {modules.map(module => {
                            const seatLocked = isSeatLockedModule(module.key, formData.role);
                            return (
                            <div key={module.key} className={`flex items-start gap-3 p-2 rounded-lg ${seatLocked ? 'bg-gray-100 opacity-70' : 'bg-gray-50'}`}>
                              <label className={`relative inline-flex items-center mt-0.5 ${seatLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                <input
                                  type="checkbox"
                                  checked={!seatLocked && (editingPermissions[module.key] || false)}
                                  onChange={() => handleEditPermissionToggle(module.key)}
                                  disabled={seatLocked}
                                  className="sr-only peer"
                                />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600 peer-disabled:opacity-50"></div>
                              </label>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">{module.label}</div>
                                <div className="text-xs text-gray-500">
                                  {seatLocked ? 'Needs a Full Access seat — change the role to grant this' : module.description}
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingEmployee(null);
                    setFormData({ email: '', first_name: '', middle_name: '', last_name: '', phone_number: '', role: 'employee', is_schedulable: true });
                  }}
                  className="btn-secondary"
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? 'Updating...' : 'Update Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'employees' && (
        <>
          {/* ✅ MODERNIZED: Glassmorphic Search & Filter */}
          <EmployeesSearchFilter
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            roleFilter={roleFilter}
            setRoleFilter={setRoleFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            filteredCount={filteredEmployees.length}
            availableRoles={[...new Set(employees.map((e) => e.role).filter(Boolean))]}
          />

          <EmployeesTable
            loading={loading}
            employees={filteredEmployees}
            sortedEmployees={sortedEmployees}
            offTodayIds={offTodayIds}
            hasActiveFilters={!!searchTerm || roleFilter !== 'all' || statusFilter !== 'all'}
            onClearFilters={() => { setSearchTerm(''); setRoleFilter('all'); setStatusFilter('all'); }}
            openDetailPanel={openDetailPanel}
            openEditForm={editEmployee}
            handleDeleteEmployee={deleteEmployee}
            handleDeactivateEmployee={handleDeactivateEmployee}
            handleReactivateEmployee={handleReactivateEmployee}
            handleToggleSchedulable={handleToggleSchedulable}
            selectedIds={selectedIds}
            toggleSelectAll={toggleSelectAll}
            toggleRow={toggleRow}
            allVisibleSelected={allVisibleSelected}
            onClearSelection={() => setSelectedIds(new Set())}
            bulkActivateCount={bulkActivateIds.length}
            bulkDeactivateCount={bulkDeactivateIds.length}
            onBulkActivate={activateSelected}
            onBulkDeactivate={deactivateSelected}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />

      {/* OLD TIME OFF SECTION REMOVED - Now using proper PTO tabs above */}


	      {/* Employee Detail Slide-over */}
	      {showDetailPanel && detailEmployee && (
	        <div className="fixed inset-0 z-50 flex">
	          <div className="fixed inset-0 bg-black/30" onClick={closeDetailPanel} />
	          <div className="ml-auto h-full w-full max-w-md bg-white shadow-xl overflow-y-auto">
	            <div className="p-4 border-b flex items-center justify-between">
	              <div className="flex items-center gap-3">
	                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
	                  <span className="text-sm font-medium text-primary-700">{getInitials(detailEmployee.full_name)}</span>
	                </div>
	                <div>
	                  <div className="text-base font-semibold text-gray-900">{detailEmployee.full_name || 'N/A'}</div>
	                  <div className="text-sm text-gray-500">{detailEmployee.email}</div>
	                </div>
	              </div>
	              <button onClick={closeDetailPanel} className="text-gray-500 hover:text-gray-700">✕</button>
	            </div>
	            <div className="p-4 space-y-4">
	              {/* Board #449, operator ruling: the quick view is DISPLAY-ONLY; employee changes
	                  go through Edit. Role was a <select> with its own Save that wrote
	                  users.role straight from the client, and the only UPDATE policy on that
	                  table is `auth_user_id = auth.uid()` (verified live 2026-09-19) — your own
	                  row and nothing else. So for every employee except yourself it matched zero
	                  rows, which Supabase reports as success with no error. The Edit flow exists
	                  precisely because of that policy: it routes the write through
	                  admin-update-employee-permissions, which holds the service role. Two ways to
	                  change one field, one of which could never work, was the whole defect. */}
	              <div>
	                <div className="text-xs text-gray-500 mb-1">Role</div>
	                <div className="text-sm text-gray-900 dark:text-white">
	                  {/* Board #2381: DISPLAY must go through getRoleDisplayName, which knows
	                      every user_role_enum value. getAvailableRoles is the ASSIGNABLE list
	                      and no longer contains `owner`, so looking a role up in it would have
	                      rendered an existing owner as the raw string "owner". */}
	                  {!detailEmployee.user_id ? 'No login' : detailEmployee.role ? getRoleDisplayName(detailEmployee.role) : '-'}
	                </div>
	              </div>

	              {detailEmployee.status==='pending_invite' && (
	                <div>
	                  <div className="text-xs text-gray-500 mb-1">Invitation</div>
	                  <div className="flex items-center gap-2">
	                    <button type="button" disabled title="Re-sending an invite is not available yet" className="btn-secondary opacity-60 cursor-not-allowed">Resend invite (coming soon)</button>
	                    <button onClick={()=>handleActivateEmployee(detailEmployee.id)} className="btn-secondary">Mark as Active</button>
	                  </div>
	                </div>
	              )}

	              <div>
	                <div className="text-xs text-gray-500 mb-1">Status</div>
	                {(() => { const s = getStatusBadge(detailEmployee); return (<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${s.bg} ${s.text}`}>{s.label}</span>); })()}
	              </div>
	              {/* Display-only for the same reason as Role above (board #449). This one went
	                  through the edge function so it could write, but it ignored
	                  user_data_result.success, so a nested failure still reported "Phone
	                  updated" — the false-success shape the full Edit flow already fixed. One
	                  editor, in Edit. */}
	              <div>
	                <div className="text-xs text-gray-500 mb-1">Phone</div>
	                <div className="text-sm text-gray-900 dark:text-white">{detailEmployee.phone ? formatPhoneDisplay(detailEmployee.phone) : '-'}</div>
	              </div>
	              <div>
	                <div className="text-xs text-gray-500 mb-1">Joined</div>
	                <div className="text-sm text-gray-900">{detailEmployee.created_at ? fmtDate(detailEmployee.created_at) : '-'}</div>
	                <div className="text-xs text-gray-400">{getTimeSinceJoined(detailEmployee.created_at)}</div>
	              </div>
	              <div>
	                <div className="text-xs text-gray-500 mb-2">Permissions</div>
	                {detailPermissions ? (
	                  <div className="grid grid-cols-2 gap-2 text-xs">
	                    {Object.keys(EMPLOYEE_MODULES).map(moduleKey => {
	                      const module = EMPLOYEE_MODULES[moduleKey];
	                      const enabled = detailPermissions[module.key];
	                      return (
	                        <div key={module.key} className="flex items-center gap-2">
	                          <span className={`inline-block w-2 h-2 rounded-full ${enabled?'bg-green-500':'bg-gray-300'}`} />
	                          <span className="text-gray-700">{module.label}</span>
	                        </div>
	                      );
	                    })}
	                  </div>
	                ) : (
	                  // Owners and admins have no employee_permissions row by design — permissionChecker
	                  // grants them everything — so "No permissions record" read as broken (board #4229).
	                  ['owner', 'admin'].includes(String(detailEmployee.role || '').toLowerCase())
	                    ? <div className="text-xs text-gray-500">Full access (Owner/Admin)</div>
	                    : <div className="text-xs text-gray-500">No permissions record</div>
	                )}
	              </div>
	              <div className="pt-2 flex flex-wrap gap-2">
	                <button onClick={()=>{ editEmployee(detailEmployee); closeDetailPanel(); }} className="btn-secondary">Edit</button>
	                {/* Reset Password. Shown to the roles the edge function accepts; it
	                    re-checks server-side and also refuses a target the caller cannot
	                    outrank, so this is convenience, not the security boundary.
	                    Hidden when the employee has no login at all — there would be nothing
	                    to reset, and Invite/Add with Password is the right action instead. */}
	                {detailEmployee.user_id
	                  && ['owner', 'admin', 'manager'].includes(String(user?.role || '').toLowerCase())
	                  && detailEmployee.user_id !== user?.id && (
	                  <button
	                    onClick={()=>{ handleResetEmployeePassword(detailEmployee); closeDetailPanel(); }}
	                    className="btn-secondary"
	                  >
	                    Reset Password
	                  </button>
	                )}
	                {/* Board #2679: Deactivate had no counterpart, so the only way back was
	                    the full Edit form. Reactivate now sits in the same action area. */}
	                {(detailEmployee.status==='active' || detailEmployee.active) ? (
	                  <button onClick={()=>{ handleDeactivateEmployee(detailEmployee.id); closeDetailPanel(); }} className="btn-secondary">Deactivate</button>
	                ) : (
	                  <button onClick={()=>{ handleReactivateEmployee(detailEmployee.id); closeDetailPanel(); }} className="btn-secondary">Reactivate</button>
	                )}
	              </div>
	            </div>
	          </div>
	        </div>
	      )}
        </>
      )}



      {/* Time Off Tab */}
      {activeTab === 'pto-management' && (
        <TimeOffPanel companyId={user?.company_id} />
      )}

      {/* Invite Modal */}
      <EmployeeInviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInvite}
      />

      {/* Board #950. The Import button was wired to console.log. It invites row by row through
          admin-invite-user — the same path Add Employee uses — rather than inserting: creating
          an auth user needs the service-role key, and that function already owns employee
          numbering, the users/employees/permissions rows and the invite email. */}
      <EmployeeImportModal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          queryClient.invalidateQueries({ queryKey: ['employees-list'] });
        }}
        existingEmails={(employees || []).map((e) => e.email)}
        onImport={importEmployeeRow}
      />

      {/* Board #2679 */}
      <ConfirmationModal isOpen={!!confirmDialog?.isOpen} {...(confirmDialog || {})} />

      {/* Confirmation Modal for Success Messages */}
      {confirmModal && (
        <ConfirmationModal
          isOpen={true}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          onClose={confirmModal.onClose}
        />
      )}
    </div>
  );
};

export default Employees;




